import React, { useCallback, useEffect, useState } from 'react';
import { 
    Box, Typography, Paper, Tabs, Tab, TextField, 
    Button, Checkbox, FormControlLabel, Grid, 
    Table, TableBody, TableCell, TableContainer, 
    TableHead, TableRow, IconButton, Snackbar, Alert, Container 
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import api from 'lib/api/api';
import ConsultantNavbar from '../../Components/Dashboard/ConsultantDashboard/ConsultantNavbar';
import { useTranslation } from "react-i18next";

const getSpecializationLabel = (key, isArabic, t) => {
  const fallbacks = {
    "consultingPage.booking.specializations.abroadStudy": isArabic ? "الدراسة في الخارج" : "Study Abroad",
    "consultingPage.booking.specializations.careerPath": isArabic ? "المسار المهني" : "Career Path",
    "consultingPage.booking.specializations.ausbildung": isArabic ? "التدريب المهني (Ausbildung)" : "Ausbildung (Vocational Training)",
    "consultingPage.booking.specializations.languagePlan": isArabic ? "خطة اللغة" : "Language Plan",
  };
  return t(key, fallbacks[key] || key);
};

const ConsultantConsultationsPage = () => {
    const { t, i18n } = useTranslation(["ui"]);
    const isArabic = i18n.language === "ar";
    const [tabIndex, setTabIndex] = useState(0);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [availableTime, setAvailableTime] = useState('');
    const [isSeries, setIsSeries] = useState(false);
    const [schedules, setSchedules] = useState([]);
    const [requests, setRequests] = useState([]);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const showSnackbar = useCallback((message, severity) => {
        setSnackbar({ open: true, message, severity });
    }, []);

    const fetchMyRequests = useCallback(async () => {
        try {
            const response = await api.get('/consultations/requests/my');
            setRequests(response.data || []);
        } catch (error) {
            console.error('Error fetching requests:', error);
            showSnackbar('Error fetching requests', 'error');
            setRequests([]);
        }
    }, [showSnackbar]);

    const fetchMySchedules = useCallback(async () => {
        try {
            const response = await api.get('/consultations/schedule/my');
            setSchedules(response.data || []);
        } catch (error) {
            console.error('Error fetching schedules:', error);
            showSnackbar('Error fetching schedules', 'error');
            setSchedules([]);
        }
    }, [showSnackbar]);

    useEffect(() => {
        if (tabIndex === 1) {
            fetchMySchedules();
        } else if (tabIndex === 2) {
            fetchMyRequests();
        }
    }, [tabIndex, fetchMyRequests, fetchMySchedules]);

    const handleTabChange = (event, newValue) => {
        setTabIndex(newValue);
    };

    const handleSubmitSchedule = async (e) => {
        e.preventDefault();
        try {
            const payload = { startDate, endDate: isSeries ? endDate : null, availableTime, isSeries };
            await api.post('/consultations/schedule', payload);
            showSnackbar('Schedule created successfully!', 'success');
            // reset form
            setStartDate('');
            setEndDate('');
            setAvailableTime('');
            setIsSeries(false);
        } catch (error) {
            console.error('Error creating schedule:', error);
            showSnackbar('Error creating schedule', 'error');
        }
    };

    const handleDelete = async (id) => {
        try {
            await api.delete(`/consultations/schedule/${id}`);
            showSnackbar('Schedule deleted successfully', 'success');
            fetchMySchedules();
        } catch (error) {
            console.error('Error deleting schedule:', error);
            showSnackbar('Error deleting schedule', 'error');
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    return (
        <div style={{ backgroundColor: '#f5f7f8', minHeight: '100vh' }}>
            <ConsultantNavbar />
            <Container sx={{ mt: 4, pb: 6 }}>
                <Typography variant="h4" fontWeight={900} gutterBottom sx={{ color: '#2D3E50', letterSpacing: -1 }}>
                    Consultation Schedules
                </Typography>
                <Paper sx={{ width: '100%', mb: 2, borderRadius: 4, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                <Tabs value={tabIndex} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tab label="Manage Schedule" />
                    <Tab label="My Schedules" />
                    <Tab label="Session Requests" />
                </Tabs>

                {/* Manage Schedule Tab */}
                {tabIndex === 0 && (
                    <Box component="form" onSubmit={handleSubmitSchedule} sx={{ p: 3 }}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <TextField 
                                    fullWidth 
                                    type="date" 
                                    label="Start Date" 
                                    InputLabelProps={{ shrink: true }} 
                                    value={startDate} 
                                    onChange={(e) => setStartDate(e.target.value)} 
                                    required 
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField 
                                    fullWidth 
                                    type="time" 
                                    label="Available Time" 
                                    InputLabelProps={{ shrink: true }} 
                                    value={availableTime} 
                                    onChange={(e) => setAvailableTime(e.target.value)} 
                                    required 
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormControlLabel 
                                    control={<Checkbox checked={isSeries} onChange={(e) => setIsSeries(e.target.checked)} />} 
                                    label="Create as a weekly series" 
                                />
                            </Grid>
                            {isSeries && (
                                <Grid item xs={12} md={6}>
                                    <TextField 
                                        fullWidth 
                                        type="date" 
                                        label="End Date" 
                                        InputLabelProps={{ shrink: true }} 
                                        value={endDate} 
                                        onChange={(e) => setEndDate(e.target.value)} 
                                        required={isSeries} 
                                    />
                                </Grid>
                            )}
                            <Grid item xs={12}>
                                <Button type="submit" variant="contained" color="primary">Save Schedule</Button>
                            </Grid>
                        </Grid>
                    </Box>
                )}

                {/* My Schedules Tab */}
                {tabIndex === 1 && (
                    <Box sx={{ p: 3 }}>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Date</TableCell>
                                        <TableCell>Time</TableCell>
                                        <TableCell>Booked Status</TableCell>
                                        <TableCell align="right">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {Array.isArray(schedules) && schedules.length > 0 ? (
                                        schedules.map((schedule) => (
                                            <TableRow key={schedule.id}>
                                                <TableCell>{schedule.availableDate}</TableCell>
                                                <TableCell>{schedule.availableTime}</TableCell>
                                                <TableCell>{schedule.isBooked ? 'Booked' : 'Available'}</TableCell>
                                                <TableCell align="right">
                                                    <IconButton color="error" onClick={() => handleDelete(schedule.id)}>
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={4} align="center" sx={{ py: 4 }}>No schedules found.</TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                )}

                {/* Session Requests Tab */}
                {tabIndex === 2 && (
                    <Box sx={{ p: 3 }}>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Student Name</TableCell>
                                        <TableCell>Email</TableCell>
                                        <TableCell>Phone</TableCell>
                                        <TableCell>Specialization</TableCell>
                                        <TableCell>Slot Requested</TableCell>
                                        <TableCell>Message</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {Array.isArray(requests) && requests.length > 0 ? (
                                        requests.map((req) => (
                                            <TableRow key={req.id}>
                                                <TableCell>{req.fullName}</TableCell>
                                                <TableCell>{req.email}</TableCell>
                                                <TableCell>{req.phone}</TableCell>
                                                <TableCell>{getSpecializationLabel(req.specialization, isArabic, t)}</TableCell>
                                                <TableCell>{`${req.slotDate} ${req.slotTime}`}</TableCell>
                                                <TableCell sx={{ maxWidth: 200, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    {req.message || '-'}
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center" sx={{ py: 4 }}>No session requests found.</TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                )}
            </Paper>

            <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}>
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
            </Container>
        </div>
    );
};

export default ConsultantConsultationsPage;
